import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, DatePicker, Descriptions, Empty, Input, Select, Table, Tag } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import BaseForm from '../../../../components/form/BaseForm';
import SearchBox from '../../../../components/outline/SearchBox';
import { PaymentSettingLog } from '../../../../lib/api/pgProviderRoute';
import { pgProviderRouteQuery } from '../../queries';
import { SettingsSection } from '../../common/SettingsSection';
import { SettingsModal } from '../../common/SettingsModal';
import { LogSnapshotStyle, PaymentSettingLogStyle } from './styles';
import { PAYMENT_LOG_LIMIT, usePaymentSettingLogFilters } from '../../hooks/usePaymentSettingLogFilters';
import { formatLogDate } from '../../utils/formatLogDate';

const targetLabel = (target: string): string =>
  target === 'payMethod' ? '결제수단·혜택' : target === 'pgProviderRoute' ? 'PG 라우팅' : target;

export const PaymentSettingLogPanel = (): React.JSX.Element => {
  const filters = usePaymentSettingLogFilters();
  const query = useQuery({ ...pgProviderRouteQuery.logs(filters.params), refetchOnMount: 'always' });
  const [selected, setSelected] = useState<PaymentSettingLog | null>(null);
  const columns: ColumnsType<PaymentSettingLog> = [
    {
      title: '변경 일시',
      dataIndex: 'createDate',
      width: 170,
      render: (date: string) => formatLogDate(date),
    },
    {
      title: '변경자',
      dataIndex: 'adminName',
      width: 120,
      render: (name: string | null, record) => name ?? (record.adminId ? '관리자 #' + record.adminId : '시스템'),
    },
    { title: '대상', dataIndex: 'target', width: 150, render: (value: string) => <Tag>{targetLabel(value)}</Tag> },
    {
      title: '변경 내용',
      dataIndex: 'changes',
      render: (changes: string[]) => (
        <div className="log-summary">
          {changes.slice(0, 2).map((change, index) => (
            <div key={index}>{change}</div>
          ))}
          {changes.length > 2 && <span className="settings-muted">외 {changes.length - 2}개 변경</span>}
        </div>
      ),
    },
    {
      title: '',
      width: 80,
      render: (_, record) => (
        <Button type="link" onClick={() => setSelected(record)}>
          상세
        </Button>
      ),
    },
  ];
  return (
    <PaymentSettingLogStyle>
      <div className="settings-panel-header">
        <div>
          <h2>설정 변경 이력</h2>
          <p>
            전체 이력에서 조건에 맞는 최신 {PAYMENT_LOG_LIMIT}건을 조회합니다. 저장 직후 이력 반영까지 잠시 걸릴 수
            있습니다.
          </p>
        </div>
        <Button icon={<ReloadOutlined />} loading={query.isFetching} onClick={() => query.refetch()}>
          새로고침
        </Button>
      </div>
      <SearchBox className="log-search-box" onSearch={filters.apply} onClear={filters.reset}>
        <BaseForm
          label="변경 대상"
          content={
            <Select
              aria-label="이력 대상"
              value={filters.target}
              options={[
                { value: 'all', label: '전체 대상' },
                { value: 'payMethod', label: '결제수단·혜택' },
                { value: 'pgProviderRoute', label: 'PG 라우팅' },
              ]}
              onChange={filters.setTarget}
            />
          }
        />
        <BaseForm
          label="변경자"
          content={
            <Input
              aria-label="변경자 이름"
              placeholder="이름 입력 (부분 검색)"
              maxLength={50}
              allowClear
              value={filters.adminName}
              onChange={(event) => filters.setAdminName(event.target.value)}
              onPressEnter={filters.apply}
            />
          }
        />
        <BaseForm
          label="변경 일시"
          subContent="한국시간(KST) 기준 · 시작일과 종료일 모두 포함 · 비우면 전체 기간"
          content={
            <DatePicker.RangePicker
              aria-label="변경 일시 범위"
              placeholder={['시작일', '종료일']}
              format="YYYY-MM-DD"
              value={filters.dateRange}
              onChange={filters.setDateRange}
            />
          }
        />
      </SearchBox>
      {query.isError ? (
        <Alert type="error" showIcon message="변경 이력을 불러오지 못했습니다. 새로고침해 주세요." />
      ) : (
        <SettingsSection
          title="변경 기록"
          action={
            <span className="settings-muted">
              검색 결과 {query.data?.logs.length ?? 0}건 · 최대 {PAYMENT_LOG_LIMIT}건
            </span>
          }
        >
          <Table
            rowKey="id"
            columns={columns}
            dataSource={query.data?.logs ?? []}
            loading={query.isLoading}
            pagination={{
              current: filters.page,
              onChange: filters.setPage,
              pageSize: 10,
              showSizeChanger: false,
              hideOnSinglePage: true,
            }}
            scroll={{ x: 680 }}
            locale={{
              emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="표시할 변경 이력이 없습니다." />,
            }}
          />
        </SettingsSection>
      )}
      <SettingsModal
        title="변경 기록 상세"
        open={selected !== null}
        onCancel={() => setSelected(null)}
        footer={<Button onClick={() => setSelected(null)}>닫기</Button>}
        width={760}
      >
        {selected && (
          <>
            <Descriptions
              column={2}
              size="small"
              items={[
                { key: 'target', label: '대상', children: targetLabel(selected.target) },
                {
                  key: 'admin',
                  label: '변경자',
                  children: selected.adminName ?? (selected.adminId ? '관리자 #' + selected.adminId : '시스템'),
                },
                {
                  key: 'date',
                  label: '변경 일시',
                  children: formatLogDate(selected.createDate, true),
                  span: 2,
                },
              ]}
            />
            <ul>
              {selected.changes.map((change, index) => (
                <li key={index}>{change}</li>
              ))}
            </ul>
            <p>이 기록은 변경 후 상태를 보관합니다. 변경 전 값이 없는 과거 기록은 전후 비교를 표시하지 않습니다.</p>
            <details>
              <summary>변경 후 전체 설정 보기</summary>
              <LogSnapshotStyle>{JSON.stringify(selected.snapshot, null, 2)}</LogSnapshotStyle>
            </details>
          </>
        )}
      </SettingsModal>
    </PaymentSettingLogStyle>
  );
};
